import { createContext } from 'preact';
import { useContext, useState, useEffect } from 'preact/hooks';
import { authService } from '../services/auth/authService';
import type { User, SignupResult } from '../services/auth/IAuthService';

interface AuthContextValue {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signup: (email: string, password: string, name?: string) => Promise<SignupResult>;
  logout: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: preact.ComponentChildren }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already logged in
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const user = await authService.checkSession();
      if (user) {
        setUser(user);
        const token = await authService.getToken();
        setToken(token);
      }
    } catch (err) {
      console.error('❌ Session check failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string, rememberMe = false) => {
    try {
      setError(null);
      const result = await authService.login(email, password, rememberMe);
      setUser(result.user);
      const token = await authService.getToken();
      setToken(token);
    } catch (err: any) {
      console.error('❌ Login error:', err);
      const message = err.message || 'An error occurred during login';
      setError(message);
      throw err;
    }
  };

  const signup = async (email: string, password: string, name?: string): Promise<SignupResult> => {
    try {
      setError(null);
      const result = await authService.signup(email, password, name);

      // If signup doesn't require verification and returns user, set it
      if (!result.requiresVerification && result.user) {
        setUser(result.user);
        const token = await authService.getToken();
        setToken(token);
      }

      return result;
    } catch (err: any) {
      const message = err.message || 'An error occurred during signup';
      setError(message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (err) {
      console.error('❌ Logout failed:', err);
    } finally {
      setUser(null);
      setToken(null);
    }
  };

  const value: AuthContextValue = {
    user,
    token,
    isAuthenticated: !!user,
    isLoading,
    login,
    signup,
    logout,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
